import { SITE } from "@/constants/testIds";

// Colour tier — matches ScoreBadge thresholds
const TIERS = {
  gold: { fill: "#D4AF37", label: "STRONG" },
  silver: { fill: "#E7E7E7", label: "MODERATE" },
  muted: { fill: "#6b7280", label: "WEAK" },
};

function tierFor(clamped) {
  if (clamped >= 75) return "gold";
  if (clamped >= 50) return "silver";
  return "muted";
}

/**
 * Premium KIRA Score visual — a radial dial rendered with pure SVG.
 * Used on the top of ReportCard and prominently on ReportPage.
 * `size` is the diameter in px. `variant="card" | "hero"` controls the palette.
 */
export function KiraScoreVisual({ score, size = 96, variant = "card" }) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const stroke = variant === "hero" ? 8 : 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - clamped / 100);

  const { fill: fillColour, label } = TIERS[tierFor(clamped)];
  const trackColour = "rgba(255,255,255,0.08)";
  const cx = size / 2;
  const cy = size / 2;

  return (
    <div
      data-testid={SITE.reportScore}
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
        <circle cx={cx} cy={cy} r={radius} stroke={trackColour} strokeWidth={stroke} fill="none" />
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          stroke={fillColour}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 700ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span
          className="font-chivo font-bold"
          style={{ color: fillColour, fontSize: size * 0.32 }}
        >
          {clamped}
        </span>
        <span
          className="font-mono uppercase tracking-[0.18em] text-kira-textMuted"
          style={{ fontSize: Math.max(8, size * 0.08), marginTop: size * 0.03 }}
        >
          {variant === "hero" ? label : "KIRA"}
        </span>
      </div>
    </div>
  );
}
